/**
 * Parabole en trois formes — calculs exacts (Boîte à outils, 1re).
 *
 * Un trinôme f(x) = ax² + bx + c (a ≠ 0) à coefficients rationnels :
 * forme canonique a(x − α)² + β avec α = −b/(2a) et β = −Δ/(4a), racines
 * exactes (−b ± √Δ)/(2a) écrites (A ± B√w)/D, forme factorisée, signe.
 * Tout est calculé en fractions : jamais « 0,333 » pour 1/3.
 */

import { add, div, exact, frac, isZero, mul, sub, value, type Frac } from '../../ui/exact';

export interface Tri {
  a: Frac;
  b: Frac;
  c: Frac;
}

export const F = (v: number) => frac(Math.round(v * 1000), 1000);
const TWO = frac(2);
const FOUR = frac(4);

export const delta = (t: Tri) => sub(mul(t.b, t.b), mul(FOUR, mul(t.a, t.c)));
export const alpha = (t: Tri) => div(frac(-t.b.p, t.b.q), mul(TWO, t.a));
export const beta = (t: Tri) => div(frac(-delta(t).p, delta(t).q), mul(FOUR, t.a));
export const evalTri = (t: Tri, x: number) => value(t.a) * x * x + value(t.b) * x + value(t.c);

export function fromCanonical(a: Frac, al: Frac, be: Frac): Tri {
  return { a, b: mul(frac(-2), mul(a, al)), c: add(mul(a, mul(al, al)), be) };
}

export function fromRoots(a: Frac, x1: Frac, x2: Frac): Tri {
  return { a, b: mul(frac(-1), mul(a, add(x1, x2))), c: mul(a, mul(x1, x2)) };
}

/* ---------- racines exactes ---------- */

function gcd(x: number, y: number): number {
  x = Math.abs(x);
  y = Math.abs(y);
  while (y) [x, y] = [y, x % y];
  return x;
}

/** n = s² × w, w sans facteur carré */
function squareFree(n: number): { s: number; w: number } {
  let s = 1;
  let w = n;
  for (let d = 2; d * d <= w; d++) {
    while (w % (d * d) === 0) {
      s *= d;
      w /= d * d;
    }
  }
  return { s, w };
}

/** (A + B√w)/D, D > 0 ; w = 1 et B = 0 pour un rationnel */
export interface Surd {
  A: number;
  B: number;
  w: number;
  D: number;
}

export type Roots =
  | { kind: 'none' }
  | { kind: 'one'; x0: Frac }
  | { kind: 'two'; x1: Surd; x2: Surd };

export const surdValue = (r: Surd) => (r.A + r.B * Math.sqrt(r.w)) / r.D;

function surd(A: number, B: number, w: number, D: number): Surd {
  if (D < 0) {
    A = -A;
    B = -B;
    D = -D;
  }
  if (B === 0 || w === 1) {
    const f = frac(A + B * (w === 1 ? 1 : 0), D);
    return { A: f.p, B: 0, w: 1, D: f.q };
  }
  const g = gcd(gcd(A, B), D) || 1;
  return { A: A / g, B: B / g, w, D: D / g };
}

export function roots(t: Tri): Roots {
  const d = delta(t);
  if (d.p < 0) return { kind: 'none' };
  if (isZero(d)) return { kind: 'one', x0: alpha(t) };
  // √(P/Q) = √(PQ)/Q = s√w/Q
  const { s, w } = squareFree(d.p * d.q);
  // −b = m/n ; 2a = r/u
  const m = -t.b.p;
  const n = t.b.q;
  const twoA = mul(TWO, t.a);
  const r = twoA.p;
  const u = twoA.q;
  const L = (n * d.q) / gcd(n, d.q);
  const M = m * (L / n);
  const S = s * (L / d.q);
  // x = (M ± S√w)/L × u/r
  let A = M * u;
  let B = S * u;
  let D = L * r;
  if (D < 0) {
    A = -A;
    B = -B;
    D = -D;
  }
  const lo = surd(A, -Math.abs(B), w, D);
  const hi = surd(A, Math.abs(B), w, D);
  return { kind: 'two', x1: lo, x2: hi };
}

/* ---------- écritures ---------- */

/** « 1/3 », « −0,5 », « 2 » */
export const fr = (f: Frac) => exact(f).plain;

/** « (3 − √17)/4 », « −1 + √2 », « 2√3 », « 5/2 » */
export function surdText(r: Surd): string {
  if (r.B === 0) return fr(frac(r.A, r.D));
  const rad = `${Math.abs(r.B) === 1 ? '' : Math.abs(r.B)}√${r.w}`;
  const sign = r.B < 0 ? '−' : '+';
  const num =
    r.A === 0
      ? `${r.B < 0 ? '−' : ''}${rad}`
      : `${r.A < 0 ? '−' : ''}${Math.abs(r.A)} ${sign} ${rad}`;
  if (r.D === 1) return num;
  return r.A === 0 ? `${num}/${r.D}` : `(${num})/${r.D}`;
}

/** le nombre est-il décimal (dénominateur 2ⁱ5ʲ) ? */
const isDecimal = (f: Frac) => !fr(f).includes('/');

/** « 1,5 » ou « 3/2 » : une expression écrit tous ses nombres de la même façon */
function nb(f: Frac, asFrac: boolean): string {
  if (!asFrac || f.q === 1) return fr(f);
  return `${f.p < 0 ? '−' : ''}${Math.abs(f.p)}/${f.q}`;
}

/** coefficient devant x² ou x : « », « − », « 2 », « −0,5 », « (1/3) » */
function coefText(f: Frac, asFrac = false): string {
  if (f.p === f.q) return '';
  if (f.p === -f.q) return '−';
  const t = nb(f, asFrac);
  return t.includes('/') ? `(${t})` : t;
}

/** terme ajouté : « + 3 », « − 2x » */
function signed(f: Frac, body: string, asFrac = false): string {
  const neg = f.p < 0;
  const abs = frac(Math.abs(f.p), f.q);
  const k = body ? coefText(abs, asFrac) : nb(abs, asFrac);
  return ` ${neg ? '−' : '+'} ${k}${body}`;
}

/** « 2x² − 3x + 1 » ; en fractions si l'un des coefficients n'est pas décimal */
export function developedText(t: Tri): string {
  const asFrac = ![t.a, t.b, t.c].every(isDecimal);
  let s = `${coefText(t.a, asFrac)}x²`;
  if (!isZero(t.b)) s += signed(t.b, 'x', asFrac);
  if (!isZero(t.c)) s += signed(t.c, '', asFrac);
  return s;
}

/** « x − 1,5 », « x + 2 », « x » */
function shift(x: Frac, asFrac = false): string {
  if (isZero(x)) return 'x';
  return x.p > 0 ? `x − ${nb(x, asFrac)}` : `x + ${nb(frac(-x.p, x.q), asFrac)}`;
}

/** « 2(x − 1)² + 3 », « −(x + 2)² », « 0,5x² − 1 », « (3/2)(x + 1/6)² − 1/24 » */
export function canonicalText(t: Tri): string {
  const al = alpha(t);
  const be = beta(t);
  const asFrac = ![t.a, al, be].every(isDecimal);
  const k = coefText(t.a, asFrac);
  const sq = isZero(al) ? 'x²' : `(${shift(al, asFrac)})²`;
  let s = `${k}${sq}`;
  if (!isZero(be)) s += ` ${be.p < 0 ? '−' : '+'} ${nb(frac(Math.abs(be.p), be.q), asFrac)}`;
  return s;
}

/** x − r écrit sans double signe : « x − 2√3 », « x + 1 − √2 », « x + (8 + √10)/6 » */
function surdShift(r: Surd, asFrac = false): string {
  if (r.B === 0) return shift(frac(r.A, r.D), asFrac);
  const rad = `${Math.abs(r.B) === 1 ? '' : Math.abs(r.B)}√${r.w}`;
  if (r.D === 1) {
    const a = r.A === 0 ? '' : ` ${r.A > 0 ? '−' : '+'} ${Math.abs(r.A)}`;
    return `x${a} ${r.B > 0 ? '−' : '+'} ${rad}`;
  }
  if (r.A === 0) return `x ${r.B > 0 ? '−' : '+'} ${rad}/${r.D}`;
  // numérateur de signe négatif en tête : on change le signe devant la fraction
  if (r.A < 0) return `x + ${surdText({ A: -r.A, B: -r.B, w: r.w, D: r.D })}`;
  return `x − ${surdText(r)}`;
}

/** « 2(x − 1)(x + 3) », « (x − 2)² », « 2x² », « 2x(x − 3) » ; null si Δ < 0 */
export function factoredText(t: Tri): string | null {
  const r = roots(t);
  if (r.kind === 'none') return null;
  if (r.kind === 'one') {
    const asFrac = ![t.a, r.x0].every(isDecimal);
    const k = coefText(t.a, asFrac);
    return isZero(r.x0) ? `${k}x²` : `${k}(${shift(r.x0, asFrac)})²`;
  }
  const rationals = [r.x1, r.x2].filter((x) => x.B === 0).map((x) => frac(x.A, x.D));
  const asFrac = ![t.a, ...rationals].every(isDecimal);
  const k = coefText(t.a, asFrac);
  const factor = (x: Surd) => `(${surdShift(x, asFrac)})`;
  // racine nulle : « 2x(x − 3) » plutôt que « 2(x)(x − 3) »
  const fs = [factor(r.x1), factor(r.x2)].map((f) => (f === '(x)' ? 'x' : f));
  fs.sort((u, v) => (u === 'x' ? -1 : v === 'x' ? 1 : 0));
  return `${k}${fs.join('')}`;
}

/** « Δ = (−3)² − 4 × 2 × 1 = 1 » */
export function deltaText(t: Tri): string {
  // négatifs et fractions entre parenthèses : (−3)², (1/3)²
  const par = (f: Frac) => (f.p < 0 || fr(f).includes('/') ? `(${fr(f)})` : fr(f));
  return `Δ = ${par(t.b)}² − 4 × ${par(t.a)} × ${par(t.c)} = ${fr(delta(t))}`;
}

/* ---------- signe ---------- */

export interface SignRow {
  /** valeurs d'annulation, dans l'ordre (écritures exactes) */
  zeros: string[];
  /** signes entre −∞, les zéros et +∞ */
  signs: Array<'+' | '−'>;
}

/** signe de a à l'extérieur des racines, du signe contraire entre elles */
export function signRow(t: Tri): SignRow {
  const sa: '+' | '−' = t.a.p > 0 ? '+' : '−';
  const so: '+' | '−' = sa === '+' ? '−' : '+';
  const r = roots(t);
  if (r.kind === 'none') return { zeros: [], signs: [sa] };
  if (r.kind === 'one') return { zeros: [fr(r.x0)], signs: [sa, sa] };
  return { zeros: [surdText(r.x1), surdText(r.x2)], signs: [sa, so, sa] };
}

/** les abscisses des racines (valeurs approchées pour la figure) */
export function rootValues(t: Tri): number[] {
  const r = roots(t);
  if (r.kind === 'none') return [];
  if (r.kind === 'one') return [value(r.x0)];
  return [surdValue(r.x1), surdValue(r.x2)];
}
